import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  PhoneIcon,
  CurrencyPoundIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  HeartIcon,
  StarIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { employeeAPI, departmentsAPI, probationaryAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const EmployeeCreateModal = ({ onClose, onSave }) => {
  const { user } = useAuth();
  const userRole = user?.role;
  const [activeTab, setActiveTab] = useState('personal');
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    // Personal Information
    employee_number: '', // Will be auto-generated if left empty
    title: 'Mr',
    first_name: '',
    middle_name: '',
    last_name: '',
    preferred_name: '',
    date_of_birth: '',
    gender: '',
    marital_status: '',
    
    // Legal Information
    national_insurance_number: '',
    passport_number: '',
    driving_license_number: '',
    right_to_work: true,
    visa_type: '',
    
    // Employment Details
    job_title: '',
    department: '',
    division: '',
    location: '',
    work_location_type: 'Office',
    employment_category: 'Permanent',
    employment_status: 'active',
    employment_type: 'full-time',
    
    // Employment Dates
    hire_date: '',
    probation_months: 6,
    probation_end_date: '',
    confirmation_date: '',
    
    // Work Schedule
    working_hours_per_week: '40',
    working_days_per_week: 5,
    start_time: '',
    end_time: '',
    break_duration_minutes: '',
    flexible_working: false,
    
    // Contact Information
    email: '',
    work_email: '',
    phone: '',
    mobile: '',
    work_phone: '',
    work_mobile: '',
    
    // Address Information
    address_line1: '',
    address_line2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'United Kingdom',
    
    // Emergency Contact
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    emergency_contact_mobile: '',
    emergency_contact_email: '',
    emergency_contact_address: '',
    
    // Salary and Benefits
    salary_amount: '',
    salary_frequency: 'monthly',
    tax_code: '1257L',
    pension_scheme: false,
    pension_contribution_percentage: '',
    student_loan_plan: '',
    currency: 'GBP',
    bonus_eligible: false,
    commission_eligible: false,
    
    // Banking Details
    bank_name: '',
    account_holder_name: '',
    sort_code: '',
    account_number: '',
    
    // Skills and Education
    highest_qualification: '',
    qualification_institution: '',
    qualification_year: '',
    professional_memberships: '',
    languages_spoken: '',
    technical_skills: '',
    soft_skills: '',
    
    // Performance
    performance_rating: '',
    career_goals: '',
    training_needs: '',
    development_plan: '',
    
    // Health and Safety
    health_conditions: '',
    allergies: '',
    medical_emergency_contact: '',
    health_and_safety_training_completed: false
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [probationSettings, setProbationSettings] = useState({
    duration_months: 6,
    available_probation_periods: [3, 6, 9, 12]
  });

  // Load departments and probation settings on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load departments
        const deptResponse = await departmentsAPI.getActive();
        setDepartments(deptResponse.data.departments || []);
        
        // Load probation settings
        const probationResponse = await probationaryAPI.getSettings();
        const responseData = probationResponse.data.data || probationResponse.data;
        const probationDefaults = responseData?.probationary_period_defaults || {};
        const defaultMonths = probationDefaults.duration_months || 6;
        const availablePeriods = probationDefaults.available_probation_periods || [3, 6, 9, 12];
        
        // Update probation settings state
        console.log('Loading probation settings in CreateModal:', { defaultMonths, availablePeriods });
        setProbationSettings({
          duration_months: defaultMonths,
          available_probation_periods: availablePeriods
        });
        
        // Update form data with default probation months
        setFormData(prev => ({
          ...prev,
          probation_months: defaultMonths
        }));
      } catch (error) {
        console.error('Error loading initial data:', error);
        // Keep default probation settings if API call fails
      }
    };
    
    loadInitialData();
  }, []);

  const tabs = [
    { id: 'personal', name: 'Personal', icon: UserIcon },
    { id: 'employment', name: 'Employment', icon: BriefcaseIcon },
    { id: 'contact', name: 'Contact', icon: PhoneIcon },
    { id: 'skills', name: 'Skills & Education', icon: AcademicCapIcon },
    { id: 'payroll', name: 'Payroll & Benefits', icon: CurrencyPoundIcon },
    { id: 'performance', name: 'Performance', icon: StarIcon },
    { id: 'health', name: 'Health & Safety', icon: HeartIcon }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only first name and last name are required
    if (!formData.first_name?.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name?.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    // Email validation - only validate format if provided
    if (formData.email && formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      alert('Please fix the validation errors before saving.');
      return;
    }
    
    setLoading(true);
    try {
      console.log("Creating new employee with data:", formData);
      
      // Filter out empty strings, null, and undefined values
      const cleanData = Object.keys(formData).reduce((acc, key) => {
        const value = formData[key];
        if (value !== null && value !== undefined && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});
      
      console.log("Cleaned data to send:", cleanData);
      
      const token = localStorage.getItem("authToken");
      console.log("Auth token available:", !!token);
      
      const result = await employeeAPI.create(cleanData);
      console.log("Create successful:", result);
      onSave();
      onClose();
    } catch (error) {
      console.error("Error creating employee:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      // Handle server-side validation errors
      if (error.response?.status === 400 && error.response?.data?.errors) {
        const serverErrors = {};
        error.response.data.errors.forEach(err => {
          // Map server error field to form field
          const field = err.path || err.param;
          if (field) {
            serverErrors[field] = err.msg || err.message;
          }
        });
        setErrors(prevErrors => ({
          ...prevErrors,
          ...serverErrors
        }));
        
        const errorFields = Object.keys(serverErrors);
        if (errorFields.length > 0) {
          alert(`Please fix the following errors:\n• ${Object.values(serverErrors).join('\n• ')}`);
        } else {
          alert("Validation failed. Please check your inputs and try again.");
        }
      } else if (error.response?.status === 400 && error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("Failed to create employee. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderInput = (name, label, type = 'text', options = null, required = false) => (
    <div className="form-group">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      {type === 'select' ? (
        <select
          value={formData[name] || ''}
          onChange={(e) => handleInputChange(name, e.target.value)}
          className={`form-select ${errors[name] ? 'border-red-300' : ''}`}
        >
          <option value="">Select {label}</option>
          {options?.map((option, index) => (
            typeof option === 'object' ? (
              <option key={option.value || index} value={option.value}>
                {option.label}
              </option>
            ) : (
              <option key={option} value={option}>{option}</option>
            )
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          value={formData[name] || ''}
          onChange={(e) => handleInputChange(name, e.target.value)}
          className={`form-input ${errors[name] ? 'border-red-300' : ''}`}
          rows={3}
        />
      ) : type === 'checkbox' ? (
        <input
          type="checkbox"
          checked={formData[name] || false}
          onChange={(e) => handleInputChange(name, e.target.checked)}
          className="form-checkbox"
        />
      ) : (
        <input
          type={type}
          value={formData[name] || ''}
          onChange={(e) => handleInputChange(name, e.target.value)}
          className={`form-input ${errors[name] ? 'border-red-300' : ''}`}
        />
      )}
      {errors[name] && (
        <p className="mt-1 text-sm text-red-600">{errors[name]}</p>
      )}
    </div>
  );

  const renderPersonalTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {renderInput('employee_number', 'Employee Number (auto-generated if empty)')}
      {renderInput('title', 'Title', 'select', [
        { value: 'Mr', label: 'Mr' },
        { value: 'Mrs', label: 'Mrs' },
        { value: 'Miss', label: 'Miss' },
        { value: 'Ms', label: 'Ms' },
        { value: 'Dr', label: 'Dr' }
      ])}
      {renderInput('first_name', 'First Name *')}
      {renderInput('middle_name', 'Middle Name')}
      {renderInput('last_name', 'Last Name *')}
      {renderInput('preferred_name', 'Preferred Name')}
      {renderInput('date_of_birth', 'Date of Birth', 'date')}
      {renderInput('gender', 'Gender', 'select', [
        { value: 'Male', label: 'Male' },
        { value: 'Female', label: 'Female' },
        { value: 'Other', label: 'Other' },
        { value: 'Prefer not to say', label: 'Prefer not to say' }
      ])}
      {renderInput('marital_status', 'Marital Status', 'select', [
        { value: 'Single', label: 'Single' },
        { value: 'Married', label: 'Married' },
        { value: 'Divorced', label: 'Divorced' },
        { value: 'Widowed', label: 'Widowed' }
      ])}
      {renderInput('national_insurance_number', 'National Insurance Number')}
      {renderInput('passport_number', 'Passport Number')}
      {renderInput('driving_license_number', 'Driving License Number')}
    </div>
  );

  const renderEmploymentTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {renderInput('job_title', 'Job Title')}
      {renderInput('department', 'Department', 'select', 
        departments && departments.length > 0 
          ? departments.map(dept => ({
              value: dept.name,
              label: `${dept.name}${dept.code ? ` (${dept.code})` : ''}`
            }))
          : [{ value: '', label: 'Loading departments...' }]
      )}
      {renderInput('division', 'Division')}
      {renderInput('location', 'Location')}
      {renderInput('work_location_type', 'Work Location Type', 'select', [
        { value: 'Office', label: 'Office' },
        { value: 'Remote', label: 'Remote' },
        { value: 'Hybrid', label: 'Hybrid' }
      ])}
      {renderInput('employment_category', 'Employment Category', 'select', [
        { value: 'Permanent', label: 'Permanent' },
        { value: 'Contract', label: 'Contract' },
        { value: 'Temporary', label: 'Temporary' },
        { value: 'Intern', label: 'Intern' }
      ])}
      {renderInput('employment_status', 'Employment Status', 'select', [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'probation', label: 'Probation' }
      ])}
      {renderInput('employment_type', 'Employment Type', 'select', [
        { value: 'full-time', label: 'Full-time' },
        { value: 'part-time', label: 'Part-time' },
        { value: 'contract', label: 'Contract' }
      ])}
      {renderInput('hire_date', 'Hire Date', 'date')}
      
      {/* Probation Period - Only Admin/HR can modify */}
      {['admin', 'hr_manager'].includes(userRole) ? (
        <div className="form-group">
          <label htmlFor="probation_months" className="form-label">
            Probation Period (Months) <span className="required">*</span>
          </label>
          <select
            id="probation_months"
            name="probation_months"
            value={formData.probation_months}
            onChange={(e) => handleInputChange('probation_months', e.target.value)}
            className={`form-input ${errors.probation_months ? 'error' : ''}`}
            required
          >
            {probationSettings.available_probation_periods.map(period => (
              <option key={period} value={period}>
                {period} Month{period !== 1 ? 's' : ''}
              </option>
            ))}
          </select>
          {errors.probation_months && <span className="error-message">{errors.probation_months}</span>}
        </div>
      ) : (
        <div className="form-group">
          <label className="form-label">Probation Period</label>
          <input
            type="text"
            value={`${formData.probation_months} months`}
            className="form-input"
            readOnly
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
          <small className="form-text">Default probation period (set by HR)</small>
        </div>
      )}
      
      <div className="form-group">
        <label className="form-label">
          Probation End Date (Calculated automatically)
        </label>
        <input
          type="date"
          value={formData.probation_end_date}
          className="form-input"
          readOnly
          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
        />
        <small className="form-text">This date will be calculated automatically based on hire date and probation months</small>
      </div>
      {renderInput('working_hours_per_week', 'Working Hours Per Week', 'number')}
      {renderInput('working_days_per_week', 'Working Days Per Week', 'number')}
    </div>
  );

  const renderContactTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {renderInput('email', 'Email', 'email')}
      {renderInput('work_email', 'Work Email', 'email')}
      {renderInput('phone', 'Phone')}
      {renderInput('mobile', 'Mobile')}
      {renderInput('work_phone', 'Work Phone')}
      {renderInput('work_mobile', 'Work Mobile')}
      
      <div className="md:col-span-2">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Address</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderInput('address_line1', 'Address Line 1')}
          {renderInput('address_line2', 'Address Line 2')}
          {renderInput('city', 'City')}
          {renderInput('county', 'County')}
          {renderInput('postcode', 'Postcode')}
          {renderInput('country', 'Country')}
        </div>
      </div>
      
      <div className="md:col-span-2">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderInput('emergency_contact_name', 'Emergency Contact Name')}
          {renderInput('emergency_contact_phone', 'Emergency Contact Phone')}
          {renderInput('emergency_contact_relationship', 'Relationship')}
          {renderInput('emergency_contact_mobile', 'Emergency Contact Mobile')}
          {renderInput('emergency_contact_email', 'Emergency Contact Email', 'email')}
          {renderInput('emergency_contact_address', 'Emergency Contact Address', 'textarea')}
        </div>
      </div>
    </div>
  );

  const renderSkillsTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {renderInput('highest_qualification', 'Highest Qualification')}
      {renderInput('qualification_institution', 'Institution')}
      {renderInput('qualification_year', 'Year of Qualification', 'number')}
      {renderInput('professional_memberships', 'Professional Memberships', 'textarea')}
      {renderInput('languages_spoken', 'Languages Spoken')}
      {renderInput('technical_skills', 'Technical Skills', 'textarea')}
      {renderInput('soft_skills', 'Soft Skills', 'textarea')}
    </div>
  );

  const renderPayrollTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {renderInput('salary_amount', 'Salary Amount', 'number')}
      {renderInput('salary_frequency', 'Salary Frequency', 'select', [
        { value: 'hourly', label: 'Hourly' },
        { value: 'daily', label: 'Daily' },
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'annually', label: 'Annually' }
      ])}
      {renderInput('tax_code', 'Tax Code')}
      {renderInput('currency', 'Currency', 'select', [
        { value: 'GBP', label: 'GBP (£)' },
        { value: 'USD', label: 'USD ($)' },
        { value: 'EUR', label: 'EUR (€)' }
      ])}
      
      <div className="form-group">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.pension_scheme || false}
            onChange={(e) => handleInputChange('pension_scheme', e.target.checked)}
            className="form-checkbox mr-2"
          />
          Pension Scheme Member
        </label>
      </div>
      
      {renderInput('pension_contribution_percentage', 'Pension Contribution %', 'number')}
      {renderInput('student_loan_plan', 'Student Loan Plan')}
      
      <div className="md:col-span-2">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Banking Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderInput('bank_name', 'Bank Name')}
          {renderInput('account_holder_name', 'Account Holder Name')}
          {renderInput('sort_code', 'Sort Code')}
          {renderInput('account_number', 'Account Number')}
        </div>
      </div>
    </div>
  );

  const renderPerformanceTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {renderInput('performance_rating', 'Performance Rating', 'select', [
        { value: 'Outstanding', label: 'Outstanding' },
        { value: 'Exceeds Expectations', label: 'Exceeds Expectations' },
        { value: 'Meets Expectations', label: 'Meets Expectations' },
        { value: 'Below Expectations', label: 'Below Expectations' },
        { value: 'Unsatisfactory', label: 'Unsatisfactory' }
      ])}
      {renderInput('career_goals', 'Career Goals', 'textarea')}
      {renderInput('training_needs', 'Training Needs', 'textarea')}
      {renderInput('development_plan', 'Development Plan', 'textarea')}
    </div>
  );

  const renderHealthTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {renderInput('health_conditions', 'Health Conditions', 'textarea')}
      {renderInput('allergies', 'Allergies', 'textarea')}
      {renderInput('medical_emergency_contact', 'Medical Emergency Contact')}
      
      <div className="form-group">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.health_and_safety_training_completed || false}
            onChange={(e) => handleInputChange('health_and_safety_training_completed', e.target.checked)}
            className="form-checkbox mr-2"
          />
          Health & Safety Training Completed
        </label>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal': return renderPersonalTab();
      case 'employment': return renderEmploymentTab();
      case 'contact': return renderContactTab();
      case 'skills': return renderSkillsTab();
      case 'payroll': return renderPayrollTab();
      case 'performance': return renderPerformanceTab();
      case 'health': return renderHealthTab();
      default: return renderPersonalTab();
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-content">
        <div className="modal-header">
          <div className="flex justify-between items-center w-full">
          <h3 className="text-lg font-semibold text-gray-900">Add New Employee</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 px-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="modal-body">
          {renderTabContent()}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="btn-primary flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <CheckIcon className="w-4 h-4" />
                <span>Create Employee</span>
              </>
            )}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeCreateModal;
